import React from 'react';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { AnimatedViews, useResponsive } from '@garnish/core';

import { useIsFoldableDevice } from '../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const LoyaltyHomeLoading = (props: Props) => {
  const { hasTopSafeArea } = props;

  const { currentBreakpoint } = useResponsive();
  const { top: safeAreaTop, right: safeAreaRight, left: safeAreaLeft } = useSafeAreaInsets();
  const isFoldableDevice = useIsFoldableDevice();

  const offerIndexes = [...Array(PLACEHOLDER_OFFERS).keys()];
  const shouldAddFoldableTopSpacing = isFoldableDevice && safeAreaTop > theme.spacing['6'];

  // ─── Styles ────────────────────────────────────────────────────────────────

  const headerContainerSafeAreaStyles = useStyle(
    () => ({
      paddingTop: shouldAddFoldableTopSpacing ? safeAreaTop - theme.spacing['6'] : undefined,
      paddingRight: safeAreaRight,
      paddingLeft: safeAreaLeft,
    }),
    [safeAreaLeft, safeAreaRight, safeAreaTop, shouldAddFoldableTopSpacing],
  );

  // ───────────────────────────────────────────────────────────────────────────

  if (currentBreakpoint.isXS) {
    return (
      <View style={styles.container}>
        <SafeAreaView edges={hasTopSafeArea ? ['top'] : []}>
          <AnimatedViews.Pulse style={styles.headerXS}>
            <View style={headerContainerSafeAreaStyles}>
              <View style={styles.pointsRowXS}>
                <View>
                  <View style={styles.greeting} />
                  <View style={styles.pointsXS} />
                </View>

                <View style={styles.scanButton} />
              </View>

              <View style={styles.progressBar} />

              <View style={styles.progressLabels}>
                <View style={styles.progressLabel} />
                <View style={styles.progressLabel} />
              </View>
            </View>

            <View style={styles.benefits} />

            <View style={styles.links}>
              <View style={styles.link} />
              <View style={styles.link} />
            </View>
          </AnimatedViews.Pulse>

          <View style={styles.offersXS}>
            <OfferCardPlaceholder index={1} />
          </View>
        </SafeAreaView>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <SafeAreaView edges={hasTopSafeArea ? ['top'] : []}>
        <AnimatedViews.Pulse style={styles.hero}>
          <View style={styles.heroContent}>
            <View style={styles.pointsCard}>
              <View style={styles.greeting} />
              <View style={styles.pointsSM} />
              <View style={styles.progressBarSM} />

              <View style={styles.progressLabels}>
                <View style={styles.progressLabel} />
                <View style={styles.progressLabel} />
              </View>

              <View style={styles.pointsCardButtons}>
                <View style={styles.pointsCardButton} />
                <View style={styles.pointsCardButton} />
              </View>
            </View>
          </View>
        </AnimatedViews.Pulse>

        <View style={styles.offersSM}>
          <AnimatedViews.Pulse index={1} style={styles.offersHeading} />

          <View style={styles.offersRow}>
            {offerIndexes.map((index) => (
              <OfferCardPlaceholder key={index} index={index + 2} />
            ))}
          </View>
        </View>
      </SafeAreaView>
    </View>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const OfferCardPlaceholder = (props: OfferCardPlaceholderProps) => {
  const { index } = props;

  const { match } = useResponsive();

  // ─── Styles ────────────────────────────────────────────────────────────────

  const offerCardStyles = [styles.offerCard, match([styles.offerCardXS, styles.offerCardSM])];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <AnimatedViews.Pulse index={index} style={offerCardStyles}>
      <View style={styles.offerImage} />
      <View style={styles.offerTitle} />
      <View style={styles.offerDescription} />
      <View style={styles.offerDetailsLink} />
    </AnimatedViews.Pulse>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PLACEHOLDER_OFFERS = 3;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: 'hidden',
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  headerXS: {
    paddingTop: theme.spacing['6'],
    paddingHorizontal: theme.spacing['6'],
  },
  pointsRowXS: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  greeting: {
    width: 152,
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  pointsXS: {
    width: 200,
    height: 32,
    marginTop: theme.spacing['2'],
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  scanButton: {
    width: 48,
    height: 48,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  progressBar: {
    height: 14,
    marginTop: theme.spacing['6'],
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  progressLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: theme.spacing['2'],
  },
  progressLabel: {
    width: 32,
    height: 10,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  benefits: {
    height: 70,
    marginTop: theme.spacing['6'],
    borderRadius: theme.radius.medium,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  links: {
    flexDirection: 'row',
    justifyContent: 'center',
    columnGap: theme.spacing['6'],
    paddingVertical: theme.spacing['8'],
  },
  link: {
    width: 76,
    height: 12,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  offersXS: {
    paddingTop: theme.spacing['6'],
    paddingHorizontal: theme.spacing['4'],
  },
  hero: {
    height: 600,
    justifyContent: 'center',
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  heroContent: {
    width: '100%',
    maxWidth: 1328,
    alignSelf: 'center',
    alignItems: 'flex-end',
    paddingHorizontal: theme.spacing['6'],
  },
  pointsCard: {
    width: '49%',
    padding: theme.spacing['8'],
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.WHITE,
  },
  pointsSM: {
    width: '64%',
    height: 40,
    marginTop: theme.spacing['4'],
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  progressBarSM: {
    height: 14,
    marginTop: theme.spacing['8'],
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  pointsCardButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    columnGap: theme.spacing['4'],
    marginTop: theme.spacing['10'],
  },
  pointsCardButton: {
    flex: 1,
    maxWidth: 140,
    height: 48,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  offersSM: {
    width: '100%',
    maxWidth: 1328,
    alignSelf: 'center',
    paddingTop: theme.spacing['20'],
    paddingHorizontal: theme.spacing['6'],
  },
  offersHeading: {
    width: 160,
    height: 40,
    marginBottom: theme.spacing['8'],
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  offersRow: {
    flexDirection: 'row',
    columnGap: theme.spacing['4'],
  },
  offerCard: {
    height: 376,
    rowGap: theme.spacing['3'],
    padding: theme.spacing['4'],
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  offerCardXS: {
    width: '100%',
  },
  offerCardSM: {
    width: 343,
  },
  offerImage: {
    height: 174,
    marginBottom: theme.spacing['3'],
    borderRadius: theme.radius.medium,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  offerTitle: {
    width: '80%',
    height: 20,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  offerDescription: {
    width: '64%',
    height: 14,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  offerDetailsLink: {
    width: 48,
    height: 14,
    marginTop: 'auto',
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  hasTopSafeArea: boolean;
};

type OfferCardPlaceholderProps = {
  index: number;
};
